import * as TreeRepository from '../Repository/TreeRepository';
import PQueue from 'p-queue';
import { Contract } from 'tree-wallet-client';
import * as ContractRepository from '../Repository/ContractRepository';
import ConfigurationService from './ConfigurationService';
// import { getToken } from './AuthService';

/**
 * Synchronizes tree photos and metadata for validator users to ensure offline availability.
 * Displays progress via the onProgress callback.
 */
export async function syncValidatorPhotos(
  contracts: Contract[],
  onProgress: (current: number, total: number) => void
) {
  // Filter for contracts where the user has the Validator role
  const validatorContracts = contracts.filter(c =>
    ContractRepository.hasRole(c.roles, ContractRepository.roles.Validator.name) ||
    ContractRepository.hasRole(c.roles, ContractRepository.roles.Agent.name) ||
    ContractRepository.hasRole(c.roles, ContractRepository.roles.Coordinator.name)
  );

  if (validatorContracts.length === 0) {
    console.log('SyncService::syncValidatorPhotos - No validator contracts found');
    return;
  }

  console.log(`SyncService::syncValidatorPhotos - Found ${validatorContracts.length} validator contract(s):`, validatorContracts.map(c => c.id));

  let totalTrees = 0;
  let processedTrees = 0;

  // First pass: calculate total trees to be synchronized for progress bar accuracy
  for (const contract of validatorContracts) {
    try {
      const response = await TreeRepository.getTreeList({
        queryParams: {
          filter: JSON.stringify({ contractId: contract.id }),
          pageSize: 1, // Minimize data for initial count fetch
          page: 1
        }
      });
      if (response.pager && response.pager.totalRecords) {
        totalTrees += response.pager.totalRecords;
      }
    } catch (error) {
      console.error(`SyncService::syncValidatorPhotos - Error fetching initial count for contract ${contract.id}`, error);
    }
  }

  console.log(`SyncService::syncValidatorPhotos - Total trees to sync across ${validatorContracts.length} contracts: ${totalTrees}`);

  // Initial progress report
  onProgress(0, totalTrees);

  const seenTreeIds = new Set<number>();
  const treeUrlBase = ConfigurationService.urls().treeUrl;
  const dataCache = await caches.open('data-cache');
  const photoQueue = new PQueue({ concurrency: 10 });
  const baseUrl = ConfigurationService.urls().baseApiUrl;

  processedTrees = 0;

  // Step 2: Fetch and process page by page
  for (const contract of validatorContracts) {
    console.log(`SyncService::syncValidatorPhotos - Processing contract ${contract.id}`);
    
    let currentPage = 1;
    let hasMore = true;
    
    // Initial fetch for the first page
    let currentResponsePromise = TreeRepository.getTreeList({
      queryParams: {
        filter: JSON.stringify({ contractId: contract.id }),
        pageSize: 500,
        page: currentPage
      }
    });

    while (hasMore) {
      const response: any = await currentResponsePromise;
      if (!response || !response.data || response.data.length === 0) {
        hasMore = false;
        continue;
      }

      const totalPages = response.pager?.totalPages || 1;
      
      // Pre-fetch the next page in background if available
      if (currentPage < totalPages) {
        currentResponsePromise = TreeRepository.getTreeList({
          queryParams: {
            filter: JSON.stringify({ contractId: contract.id }),
            pageSize: 500,
            page: currentPage + 1
          }
        });
      } else {
        hasMore = false;
      }

      // Process current batch
      for (const tree of response.data) {
        if (seenTreeIds.has(tree.id)) continue;
        seenTreeIds.add(tree.id);

        // 1. Save tree metadata to cache
        const treeDetailUrl = `${treeUrlBase}/${tree.id}`;
        const cachedResponse = new Response(JSON.stringify(tree), {
          headers: { 'Content-Type': 'application/json' },
          status: 200,
          statusText: 'OK'
        });
        await dataCache.put(treeDetailUrl, cachedResponse);

        // 2. Queue photo downloads (don't await them here)
        if (tree.photos && Array.isArray(tree.photos)) {
          tree.photos.forEach((photo: any) => {
            if (photo.imageUrl) {
              const urls = [photo.imageUrl];
              const thumbUrl = photo.imageUrl.replace(/\.(jpe?g|png|webp|gif|svg)$/i, '_thumb.$1');
              if (thumbUrl.toLowerCase() !== photo.imageUrl.toLowerCase()) {
                urls.push(thumbUrl);
              }

              urls.forEach(url => {
                photoQueue.add(async () => {
                  try {
                    const fullUrl = (url.startsWith('http') || url.startsWith('data:'))
                      ? url
                      : `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
                    await fetch(fullUrl, { mode: 'no-cors' });
                  } catch (err) {
                    console.warn(`SyncService::syncValidatorPhotos - Photo fetch failed for ${url}`, err);
                  }
                });
              });
            }
          });
        }

        processedTrees++;
        // Update progress immediately for each tree processed
        onProgress(processedTrees, totalTrees);
      }

      currentPage++;
    }
  }

  // Final signal and exit. Photo queue will continue to drain in background.
  console.log(`SyncService::syncValidatorPhotos - Metadata sync complete. ${seenTreeIds.size} trees processed.`);
  onProgress(totalTrees, totalTrees);
}
